import React, { useEffect, useState, useRef, useContext } from "react";
import {
  IComponentNode,
  DataSourceType,
  DisplayComponentType,
} from "../../types/pageBuilder.types";
import { useDynamicSnippetService } from "../../services/dynamicSnippetService";
import {
  getOriginalImageUrl,
  getResizedImageUrl,
  ImagePresets,
} from "../../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../../hooks/useStoreLogoPlaceholder";
import {
  getAnimationStyles,
  shouldTriggerOnScroll,
} from "../../utils/animationUtils";
import { ModalLanguageContext } from "../../context/ModalLanguageContext";
import { SupportedLanguage } from "../../types/translations.types";
import { useTranslation } from "react-i18next";
import * as FaIcons from "react-icons/fa";
import * as Fa6Icons from "react-icons/fa6";
import { ProductCard } from "../ui/ProductCard";
import { calculateProductCardPricing } from "../../utils/inventoryUtils";
import { StoreContext } from "../../context/StoreContext";
import { useCurrency } from "../../context/CurrencyContext";
import "react-quill/dist/quill.snow.css";
import { ensureFontsLoaded } from "../../utils/fontLoader";
import {
  createSanitizedFontMap,
  extractFontFamiliesFromHtml,
} from "../../utils/fontCatalog";

// Tailwind safelist: Classes used in platform zone templates (stored in database)
// These classes are dynamically applied via className prop from component.props.className
// bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 min-h-[90vh] md:min-h-screen flex items-center justify-center relative overflow-hidden py-[60px] px-4 md:py-20 md:px-4
// max-w-7xl mx-auto text-center relative z-10
// font-sans text-4xl md:text-5xl font-bold text-white mb-6 block
// font-sans text-3xl md:text-5xl font-bold bg-gradient-to-r from-yellow-300 to-pink-400 bg-clip-text text-transparent mb-4 md:mb-6 block
// py-12 px-4 md:py-16 md:px-4 bg-white
// max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8
// text-center font-sans text-4xl md:text-5xl font-bold text-gray-900 mb-2 md:mb-2
// font-sans text-base md:text-lg text-gray-600
// py-16 md:py-24 px-4 bg-gradient-to-b from-white to-gray-50
// text-3xl md:text-5xl font-bold text-gray-900 mb-4
// text-lg md:text-xl text-gray-600 max-w-2xl mx-auto
// max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8
// bg-white p-6 md:p-8
// font-sans text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4
// text-gray-600 leading-relaxed
// flex flex-col gap-4
// font-sans text-base md:text-lg text-gray-700
// bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-8
// bg-white p-8
// text-2xl md:text-3xl font-bold text-gray-900 mb-4
// text-lg text-gray-600 mb-8 leading-relaxed
// block w-full text-center px-6 py-4 bg-indigo-600 text-white text-lg font-bold no-underline border-none cursor-pointer hover:bg-indigo-700 transition-colors
// inline-flex items-center px-8 py-4 bg-white text-blue-500 font-sans text-lg font-bold no-underline border-none cursor-pointer hover:bg-gray-50 transition-colors
// inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-sans text-lg font-semibold no-underline border-2 border-white/30 cursor-pointer hover:bg-white/20 transition-colors
// py-12 md:py-24 px-4 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700
// max-w-4xl mx-auto text-center
// text-3xl md:text-5xl font-bold text-white mb-6
// text-lg md:text-2xl text-white/90 mb-8
// inline-flex items-center px-10 py-5 bg-white text-blue-500 text-lg md:text-xl font-bold no-underline border-none cursor-pointer hover:bg-gray-50 transition-colors

// Helper function to process templating syntax ({{fieldName}})
function processTemplate(
  text: string,
  data: any,
  _variableName: string = "item",
): string {
  if (!text || !data) return text;

  // Replace {{fieldName}} with data.fieldName
  // Also support nested properties like {{field.nested}}
  return text.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    const keys = path.trim().split(".");
    let value = data;

    for (const key of keys) {
      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        return match; // Return original if path not found
      }
    }

    return value != null ? String(value) : match;
  });
}

// Helper function to get translated value for IComponentNode
// IComponentNode stores values in props, not at root level
function getComponentTranslatedValue(
  component: IComponentNode,
  field: string,
  language: SupportedLanguage,
  loopItem?: any,
  loopItemVariable: string = "item",
): string {
  let text = "";

  // For English, use props directly
  if (language === "en") {
    text = component.props[field] || "";
  } else {
    // For other languages, check translations
    const translations = component.translations?.[field];
    if (translations && translations[language]) {
      text = translations[language] || "";
    } else {
      // Fallback to English (props)
      text = component.props[field] || "";
    }
  }

  // Process templating if loopItem is provided
  if (loopItem && text) {
    text = processTemplate(text, loopItem, loopItemVariable);
  }

  return text;
}

// Helper component to render translated text
const TranslatedText: React.FC<{
  component: IComponentNode;
  field: string;
  defaultText: string;
  style?: React.CSSProperties;
  className?: string;
  isHTML?: boolean;
  loopItem?: any;
  loopItemVariable?: string;
}> = ({
  component,
  field,
  defaultText,
  style,
  className,
  isHTML = false,
  loopItem,
  loopItemVariable = "item",
}) => {
  // Get language from ModalLanguageContext (for page builder) or i18n (for public view)
  const modalLanguageContext = useContext(ModalLanguageContext);
  const { i18n } = useTranslation();

  // Extract base language code (e.g., "en-US" -> "en", "de-DE" -> "de")
  let activeLanguage: string;
  if (modalLanguageContext?.currentLanguage) {
    // In page builder, use ModalLanguageContext
    activeLanguage = modalLanguageContext.currentLanguage;
  } else {
    // On public site, use i18n language
    const i18nLang = i18n.language || "en";
    activeLanguage = i18nLang.split("-")[0].toLowerCase();
  }

  const language: SupportedLanguage = (
    ["en", "de", "ar"].includes(activeLanguage) ? activeLanguage : "en"
  ) as SupportedLanguage;

  const textValue =
    getComponentTranslatedValue(
      component,
      field,
      language,
      loopItem,
      loopItemVariable,
    ) || defaultText;

  if (isHTML) {
    const containsHTML = textValue && /<[a-z][\s\S]*>/i.test(textValue);
    const finalClassName = className
      ? containsHTML
        ? `ql-snow ${className}`
        : className
      : containsHTML
        ? "ql-snow"
        : "";
    return (
      <div style={style} className={finalClassName || undefined}>
        {containsHTML ? (
          <div
            className="ql-editor"
            dangerouslySetInnerHTML={{ __html: textValue }}
          />
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: textValue }}
            className={className || undefined}
          />
        )}
      </div>
    );
  }

  return (
    <span style={style} className={className || undefined}>
      {textValue}
    </span>
  );
};

interface PageRendererProps {
  layout: IComponentNode;
  isPreview?: boolean;
}

export const PageRenderer: React.FC<PageRendererProps> = ({
  layout,
  isPreview = false,
}) => {
  return <ComponentRenderer component={layout} isPreview={isPreview} />;
};

interface ComponentRendererProps {
  component: IComponentNode;
  isPreview?: boolean;
}

interface ComponentRendererProps {
  component: IComponentNode;
  isPreview?: boolean;
  loopItem?: any; // Data from parent loop component
  loopItemVariable?: string; // Variable name for loop item (default: "item")
}

const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  component,
  isPreview = false,
  loopItem,
  loopItemVariable = "item",
}) => {
  const [snippetData, setSnippetData] = useState<any[]>([]);
  const [loopData, setLoopData] = useState<any[]>([]);
  const [dynamicSnippetData, setDynamicSnippetData] = useState<any[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [loadingLoop, setLoadingLoop] = useState(false);
  const [loadingDynamicSnippet, setLoadingDynamicSnippet] = useState(false);
  const snippetService = useDynamicSnippetService();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  // Safely access store context - may be undefined if not within StoreProvider (e.g., platform pages)
  const storeContext = useContext(StoreContext);
  const store = storeContext?.store || null;
  const { location } = useCurrency();
  const elementRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (component.snippetId) {
      loadSnippetData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component.snippetId]);

  // Load loop data if component is a loop
  useEffect(() => {
    if (component.type === "loop" && component.loopConfig) {
      loadLoopData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component.loopConfig, isPreview, store?._id]);

  // Load dynamic snippet data if component is a dynamic-snippet
  useEffect(() => {
    if (component.type === "dynamic-snippet" && component.props?.dataSource) {
      loadDynamicSnippetData();
    } else if (component.type === "dynamic-snippet") {
      // Reset data if dataSource is removed
      setDynamicSnippetData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    component.type,
    component.props?.dataSource,
    component.props?.filters,
    component.props?.displayComponent,
    store?._id,
    isPreview,
  ]);

  // Handle carousel scroll position checking
  useEffect(() => {
    if (
      component.type === "dynamic-snippet" &&
      component.props?.displayComponent === "carousel"
    ) {
      const checkScroll = () => {
        if (carouselRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
          setCanScrollLeft(scrollLeft > 0);
          setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
      };

      checkScroll();
      const carousel = carouselRef.current;
      if (carousel) {
        carousel.addEventListener("scroll", checkScroll);
        // Also check on resize
        window.addEventListener("resize", checkScroll);
        return () => {
          carousel.removeEventListener("scroll", checkScroll);
          window.removeEventListener("resize", checkScroll);
        };
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component.type, component.props?.displayComponent, dynamicSnippetData]);

  // Handle scroll-triggered animations
  useEffect(() => {
    if (!component.animations || !shouldTriggerOnScroll(component.animations)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    const element = elementRef.current;

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [component.animations]);

  const loadSnippetData = async () => {
    if (!component.snippetId) return;

    try {
      const result = await snippetService.executeSnippet(component.snippetId);
      setSnippetData(result.data);
    } catch (error) {
      console.error("Error loading snippet data:", error);
    }
  };

  const loadLoopData = async () => {
    if (!component.loopConfig) return;

    const { dataSource, filters = {}, limit } = component.loopConfig;

    // For products and categories, storeId is required
    // But for public rendering, the backend can get storeId from storeMiddleware
    // So we should still try to call the API even if store context is not available
    if (
      (dataSource === "products" || dataSource === "categories") &&
      !store?._id &&
      isPreview
    ) {
      // Only skip in preview mode if store is not available
      // In public mode, let the backend handle storeId from middleware
      console.warn(
        "Store ID required for products/categories data source in preview mode",
      );
      return;
    }

    try {
      setLoadingLoop(true);

      // Use public endpoint for page rendering (with caching), protected endpoint for preview/editing
      if (isPreview) {
        // In preview mode, use the protected endpoint
        const result = await snippetService.executeDataSource(
          dataSource,
          filters,
          { limit },
        );
        setLoopData(result.data);
      } else {
        // In public rendering, use the public endpoint with caching
        // Backend can get storeId from storeMiddleware if not provided in body
        const storeId = store?._id;
        const result = await snippetService.executeDataSourcePublic(
          dataSource,
          filters,
          { limit, ...(storeId && { storeId }) },
        );
        setLoopData(result.data);
      }
    } catch (error) {
      console.error("Error loading loop data:", error);
      setLoopData([]);
    } finally {
      setLoadingLoop(false);
    }
  };

  const loadDynamicSnippetData = async () => {
    if (!component.props?.dataSource) return;

    const dataSource = component.props.dataSource as DataSourceType;

    // For products and categories, storeId is required
    // But for public rendering, the backend can get storeId from storeMiddleware
    // So we should still try to call the API even if store context is not available
    if (
      (dataSource === "products" || dataSource === "categories") &&
      !store?._id &&
      isPreview
    ) {
      // Only skip in preview mode if store is not available
      // In public mode, let the backend handle storeId from middleware
      console.warn(
        "Store ID required for products/categories data source in preview mode",
      );
      return;
    }

    try {
      setLoadingDynamicSnippet(true);
      const filters = component.props.filters || {};
      const limit = filters.limit || 12;

      // Use public endpoint for page rendering (with caching), protected endpoint for preview/editing
      if (isPreview) {
        // In preview mode, use the protected endpoint
        const result = await snippetService.executeDataSource(
          dataSource,
          filters,
          { limit },
        );
        setDynamicSnippetData(result.data);
      } else {
        // In public rendering, use the public endpoint with caching
        // Backend can get storeId from storeMiddleware if not provided in body
        const storeId = store?._id;
        const result = await snippetService.executeDataSourcePublic(
          dataSource,
          filters,
          { limit, ...(storeId && { storeId }) },
        );
        setDynamicSnippetData(result.data);
      }
    } catch (error) {
      console.error("Error loading dynamic snippet data:", error);
      setDynamicSnippetData([]);
    } finally {
      setLoadingDynamicSnippet(false);
    }
  };

  const buildStyles = (): React.CSSProperties => {
    // For dynamic snippets when not in preview, completely ignore default editor styles
    // Users should control styling through the page builder, not have default editor styles
    // Also filter editor styles when dynamic snippet has content (even in preview mode)
    const isDynamicSnippetWithContent =
      component.type === "dynamic-snippet" &&
      component.props?.dataSource &&
      dynamicSnippetData.length > 0 &&
      !loadingDynamicSnippet;

    if (
      component.type === "dynamic-snippet" &&
      (!isPreview || isDynamicSnippetWithContent)
    ) {
      // Start with empty styles - only apply user-defined responsive styles
      const mergedStyles: React.CSSProperties = {};

      // Apply responsive styles based on viewport (user-defined styles only)
      const viewportWidth =
        typeof window !== "undefined" ? window.innerWidth : 1024;
      if (viewportWidth < 768 && component.styles?.mobile) {
        Object.assign(mergedStyles, component.styles.mobile);
      } else if (viewportWidth < 1024 && component.styles?.tablet) {
        Object.assign(mergedStyles, component.styles.tablet);
      } else if (component.styles?.desktop) {
        Object.assign(mergedStyles, component.styles.desktop);
      }

      // Filter out editor-specific placeholder styles when content is loaded
      if (isDynamicSnippetWithContent) {
        delete mergedStyles.border;
        delete mergedStyles.borderWidth;
        delete mergedStyles.borderStyle;
        delete mergedStyles.borderColor;
        delete mergedStyles.backgroundColor;
        delete mergedStyles.background;
        delete mergedStyles.padding;
        delete mergedStyles.minHeight;
        delete mergedStyles.display;
        delete mergedStyles.alignItems;
        delete mergedStyles.justifyContent;
        delete mergedStyles.flexDirection;
      }

      return mergedStyles;
    }

    // For all other components or preview mode, use normal style merging
    const defaultStyles = component.styles?.default || {};
    const mergedStyles: React.CSSProperties = { ...defaultStyles };

    // Apply responsive styles based on viewport
    // In production, use actual breakpoint detection
    const viewportWidth =
      typeof window !== "undefined" ? window.innerWidth : 1024;
    if (viewportWidth < 768 && component.styles?.mobile) {
      Object.assign(mergedStyles, component.styles.mobile);
    } else if (viewportWidth < 1024 && component.styles?.tablet) {
      Object.assign(mergedStyles, component.styles.tablet);
    } else if (component.styles?.desktop) {
      Object.assign(mergedStyles, component.styles.desktop);
    }

    // Apply animations
    const animationStyles = getAnimationStyles(component.animations);
    if (component.animations?.trigger === "onScroll" && !isVisible) {
      // Hide element until it's visible
      mergedStyles.opacity = 0;
    } else {
      Object.assign(mergedStyles, animationStyles);
    }

    return mergedStyles;
  };

  // Get language for translations
  const modalLanguageContext = useContext(ModalLanguageContext);
  const { i18n } = useTranslation();
  const activeLanguage =
    modalLanguageContext?.currentLanguage ||
    (i18n.language.split("-")[0] as SupportedLanguage);
  const language: SupportedLanguage = ["en", "de", "ar"].includes(
    activeLanguage,
  )
    ? activeLanguage
    : "en";

  const QUILL_SANITIZED_FONT_MAP = useRef<Map<string, string> | null>(null);
  if (!QUILL_SANITIZED_FONT_MAP.current) {
    QUILL_SANITIZED_FONT_MAP.current = createSanitizedFontMap();
  }

  // Load fonts on demand for rendered zone content (Quill HTML / inline font-family)
  useEffect(() => {
    if (component.type !== "text") return;

    const html = getComponentTranslatedValue(
      component,
      "text",
      language,
      loopItem,
      loopItemVariable,
    );

    if (!html) return;
    if (!/<[a-z][\s\S]*>/i.test(html)) return;

    const fontFamilies = extractFontFamiliesFromHtml(
      html,
      QUILL_SANITIZED_FONT_MAP.current as Map<string, string>,
    );

    if (fontFamilies.length === 0) return;
    ensureFontsLoaded(fontFamilies).catch(() => {
      // Non-fatal; fallbacks render
    });
  }, [component, language, loopItem, loopItemVariable]);

  const renderComponent = () => {
    const styles = buildStyles();
    const className = component.props?.className || undefined;
    const wrapperProps: any = {
      ref:
        component.animations?.trigger === "onScroll" ? elementRef : undefined,
      style: styles,
      ...(className && { className }),
    };

    switch (component.type) {
      case "container":
        // If container has href, render as link
        if (component.props?.href) {
          return (
            <a
              href={component.props.href}
              style={styles}
              className={className || undefined}
              {...(wrapperProps.ref ? { ref: wrapperProps.ref } : {})}
            >
              {component.children?.map((child) => (
                <ComponentRenderer
                  key={child.id}
                  component={child}
                  isPreview={isPreview}
                />
              ))}
            </a>
          );
        }
        return (
          <div {...wrapperProps}>
            {component.children?.map((child) => (
              <ComponentRenderer
                key={child.id}
                component={child}
                isPreview={isPreview}
                loopItem={loopItem}
                loopItemVariable={loopItemVariable}
              />
            ))}
          </div>
        );
      case "loop":
        if (loadingLoop) {
          return <div {...wrapperProps}>Loading...</div>;
        }
        const itemVariable = component.loopConfig?.itemVariable || "item";
        return (
          <div {...wrapperProps}>
            {loopData.map((item, index) => (
              <React.Fragment key={index}>
                {component.children?.map((child) => (
                  <ComponentRenderer
                    key={`${child.id}-${index}`}
                    component={child}
                    isPreview={isPreview}
                    loopItem={item}
                    loopItemVariable={itemVariable}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        );
      case "text":
        return (
          <div {...wrapperProps}>
            <TranslatedText
              component={component}
              field="text"
              defaultText=""
              isHTML={true}
              className={className}
              loopItem={loopItem}
              loopItemVariable={loopItemVariable}
            />
          </div>
        );
      case "heading":
        const HeadingTag =
          `h${component.props.level || 1}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag {...wrapperProps} {...(className && { className })}>
            <TranslatedText
              component={component}
              field="text"
              defaultText=""
              loopItem={loopItem}
              loopItemVariable={loopItemVariable}
            />
          </HeadingTag>
        );
      case "image":
        // Get translated image src if available
        let imageSrc = component.props.src || storeLogoPlaceholder;
        if (language !== "en") {
          const translations = component.translations?.src;
          if (translations && translations[language]) {
            imageSrc = translations[language];
          }
        }
        const imageUrl =
          imageSrc.startsWith("http") || imageSrc.startsWith("/")
            ? imageSrc
            : getOriginalImageUrl(imageSrc, storeLogoPlaceholder);
        const imageStyles: React.CSSProperties = {
          ...styles,
          objectFit: component.props.objectFit || styles.objectFit || "cover",
        };
        const imageRef =
          component.animations?.trigger === "onScroll"
            ? (elementRef as React.RefObject<HTMLImageElement>)
            : undefined;
        return (
          <img
            ref={imageRef}
            src={imageUrl}
            alt={component.props.alt || ""}
            style={imageStyles}
            {...(className && { className })}
            loading={
              component.props.loading === "lazy" ||
              component.props.loading === true
                ? "lazy"
                : "eager"
            }
          />
        );
      case "button":
        return (
          <a href={component.props.href || "#"} {...wrapperProps}>
            <TranslatedText
              component={component}
              field="text"
              defaultText="Button"
            />
          </a>
        );
      case "link":
        return (
          <a href={component.props.href || "#"} {...wrapperProps}>
            <TranslatedText
              component={component}
              field="text"
              defaultText="Link"
            />
          </a>
        );
      case "list":
        return (
          <ul style={styles} {...(className && { className })}>
            {(component.props.items || []).map(
              (item: string, index: number) => (
                <li key={index}>{item}</li>
              ),
            )}
          </ul>
        );
      case "ordered-list":
        return (
          <ol style={styles} {...(className && { className })}>
            {(component.props.items || []).map(
              (item: string, index: number) => (
                <li key={index}>{item}</li>
              ),
            )}
          </ol>
        );
      case "quote":
        return (
          <blockquote style={styles} {...(className && { className })}>
            <p>
              <TranslatedText
                component={component}
                field="text"
                defaultText=""
              />
            </p>
            {component.props.author && (
              <cite>
                —{" "}
                <TranslatedText
                  component={component}
                  field="author"
                  defaultText={component.props.author || ""}
                />
              </cite>
            )}
          </blockquote>
        );
      case "divider":
        return <hr style={styles} {...(className && { className })} />;
      case "code":
        return (
          <pre style={styles} {...(className && { className })}>
            <code>{component.props.code || ""}</code>
          </pre>
        );
      case "card":
        return (
          <div style={styles} {...(className && { className })}>
            {component.props.title && (
              <h3>
                <TranslatedText
                  component={component}
                  field="title"
                  defaultText={component.props.title || ""}
                />
              </h3>
            )}
            <p>
              <TranslatedText
                component={component}
                field="content"
                defaultText={component.props.content || ""}
              />
            </p>
          </div>
        );
      case "icon": {
        const iconName = component.props.iconName || "FaCheck";
        const iconLibrary = component.props.iconLibrary || "fa";
        const iconColor = component.props.color || "#4F46E5";
        const iconSize = parseInt(component.props.size || "24", 10);

        // Get icon from appropriate library
        const IconLibrary = iconLibrary === "fa6" ? Fa6Icons : FaIcons;
        const IconComponent = (IconLibrary as any)[iconName] || FaIcons.FaCheck;

        return (
          <span
            {...wrapperProps}
            style={{
              ...styles,
              color: iconColor,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconComponent size={iconSize} style={{ color: iconColor }} />
          </span>
        );
      }
      case "grid":
        if (component.snippetId && snippetData.length > 0) {
          return (
            <div {...wrapperProps}>
              {snippetData.map((_item, index) => (
                <div key={index}>{/* Render product/item card */}</div>
              ))}
            </div>
          );
        }
        return (
          <div {...wrapperProps}>
            {component.children?.map((child) => (
              <ComponentRenderer
                key={child.id}
                component={child}
                isPreview={isPreview}
                loopItem={loopItem}
                loopItemVariable={loopItemVariable}
              />
            ))}
          </div>
        );
      case "carousel":
        if (component.snippetId && snippetData.length > 0) {
          return (
            <div
              style={{ ...styles, display: "flex", overflowX: "auto" }}
              {...(className && { className })}
            >
              {snippetData.map((_item, index) => (
                <div key={index} style={{ minWidth: "300px" }}>
                  {/* Render product/item card */}
                </div>
              ))}
            </div>
          );
        }
        return (
          <div
            style={{ ...styles, display: "flex", overflowX: "auto" }}
            className={className}
          >
            {component.children?.map((child) => (
              <div key={child.id} style={{ minWidth: "300px" }}>
                <ComponentRenderer component={child} isPreview={isPreview} />
              </div>
            ))}
          </div>
        );
      case "spacer":
        return <div style={styles} {...(className && { className })} />;
      case "video":
        return (
          <video
            src={component.props.src}
            controls={component.props.controls}
            autoPlay={component.props.autoplay}
            style={styles}
            {...(className && { className })}
          />
        );
      case "dynamic-snippet": {
        const displayComponent =
          (component.props?.displayComponent as DisplayComponentType) || "grid";
        const hasDataSource = component.props?.dataSource;

        // Show placeholder if no data source is configured
        // Only show placeholder styling in preview/editor mode
        if (!hasDataSource) {
          const placeholderStyles = isPreview
            ? {
                ...styles,
                minHeight: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "0.5rem",
              }
            : { display: "none" }; // Hide completely for users if not configured
          return (
            <div {...wrapperProps} style={placeholderStyles}>
              <div style={{ fontSize: "3rem", opacity: 0.3 }}>📊</div>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "0.875rem",
                  textAlign: "center",
                }}
              >
                {component.props?.placeholder ||
                  "Configure dynamic content in the Data tab"}
              </p>
            </div>
          );
        }

        // Show loading state
        if (loadingDynamicSnippet) {
          const loadingStyles = isPreview
            ? {
                ...styles,
                minHeight: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }
            : {}; // No special styling for users
          return (
            <div {...wrapperProps} style={loadingStyles}>
              <p style={{ color: "#6b7280" }}>Loading...</p>
            </div>
          );
        }

        // Show empty state (but only if we've tried to load - check if loading has completed)
        if (dynamicSnippetData.length === 0 && !loadingDynamicSnippet) {
          const emptyStyles = isPreview
            ? {
                ...styles,
                minHeight: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "0.5rem",
              }
            : { display: "none" }; // Hide empty state for users
          return (
            <div {...wrapperProps} style={emptyStyles}>
              <p style={{ color: "#6b7280" }}>No data available</p>
              {!store?._id && (
                <p style={{ color: "#9ca3af", fontSize: "0.75rem" }}>
                  Store context required
                </p>
              )}
            </div>
          );
        }

        const dataSource = component.props.dataSource as DataSourceType;

        // Render based on display component type
        switch (displayComponent) {
          case "grid": {
            const columns = component.props?.columns || {
              mobile: 1,
              tablet: 2,
              desktop: 3,
            };
            // For user-facing pages, don't include default editor styles
            const baseStyles = !isPreview ? {} : styles;
            const gridStyle: React.CSSProperties = {
              display: "grid",
              gap: component.props?.gap || "1rem",
              gridTemplateColumns: `repeat(${columns.desktop || 3}, 1fr)`,
              width: "100%",
              ...baseStyles,
            };

            return (
              <div {...wrapperProps} style={gridStyle} className={className}>
                {dynamicSnippetData.map((item, index) => {
                  if (dataSource === "products") {
                    const image =
                      item.images?.find((img: any) => img.isPrimary) ||
                      item.images?.[0];
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.productCard,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const locationId = location?.locationId;

                    return (
                      <div
                        key={item._id || index}
                        style={{ minWidth: 0, width: "100%" }}
                      >
                        <ProductCard
                          id={item._id}
                          title={item.title}
                          image={imageUrl}
                          currency={location?.currency || "USD"}
                          locationId={locationId}
                          product={item}
                          href={`/products/${item.handle || item._id}`}
                        />
                      </div>
                    );
                  } else if (dataSource === "categories") {
                    return (
                      <div
                        key={item._id || index}
                        style={{ minWidth: 0, width: "100%" }}
                      >
                        <h3
                          style={{
                            fontSize: "1.25rem",
                            fontWeight: "bold",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {item.name}
                        </h3>
                        {item.description && (
                          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          }
          case "carousel": {
            // For user-facing pages, don't include default editor styles
            const baseStyles = !isPreview ? {} : styles;

            // Scroll handlers
            const scrollLeft = () => {
              if (carouselRef.current) {
                const cardWidth = 300 + 16; // minWidth + gap
                carouselRef.current.scrollBy({
                  left: -cardWidth,
                  behavior: "smooth",
                });
              }
            };

            const scrollRight = () => {
              if (carouselRef.current) {
                const cardWidth = 300 + 16; // minWidth + gap
                carouselRef.current.scrollBy({
                  left: cardWidth,
                  behavior: "smooth",
                });
              }
            };

            return (
              <div
                {...wrapperProps}
                style={{ ...baseStyles, position: "relative" }}
              >
                {canScrollLeft && (
                  <button
                    onClick={scrollLeft}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all"
                    aria-label="Previous"
                  >
                    <FaIcons.FaChevronLeft className="text-gray-700" />
                  </button>
                )}
                <div
                  ref={carouselRef}
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    gap: "1rem",
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                  className="[&::-webkit-scrollbar]:hidden"
                >
                  {dynamicSnippetData.map((item, index) => {
                    if (dataSource === "products") {
                      const image =
                        item.images?.find((img: any) => img.isPrimary) ||
                        item.images?.[0];
                      const imageUrl = image
                        ? getResizedImageUrl(
                            image.url,
                            ImagePresets.productCard,
                            storeLogoPlaceholder,
                          )
                        : storeLogoPlaceholder;
                      const locationId = location?.locationId;

                      return (
                        <div
                          key={item._id || index}
                          style={{
                            minWidth: "300px",
                            scrollSnapAlign: "start",
                          }}
                        >
                          <ProductCard
                            id={item._id}
                            title={item.title}
                            image={imageUrl}
                            currency={location?.currency || "USD"}
                            locationId={locationId}
                            product={item}
                            href={`/products/${item.handle || item._id}`}
                          />
                        </div>
                      );
                    } else if (dataSource === "categories") {
                      return (
                        <div
                          key={item._id || index}
                          style={{
                            minWidth: "300px",
                            scrollSnapAlign: "start",
                          }}
                        >
                          <h3
                            style={{
                              fontSize: "1.25rem",
                              fontWeight: "bold",
                              marginBottom: "0.5rem",
                            }}
                          >
                            {item.name}
                          </h3>
                          {item.description && (
                            <p
                              style={{ color: "#6b7280", fontSize: "0.875rem" }}
                            >
                              {item.description}
                            </p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
                {canScrollRight && (
                  <button
                    onClick={scrollRight}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all"
                    aria-label="Next"
                  >
                    <FaIcons.FaChevronRight className="text-gray-700" />
                  </button>
                )}
              </div>
            );
          }
          case "list": {
            // For user-facing pages, don't include default editor styles
            const baseStyles = !isPreview ? {} : styles;
            return (
              <div
                {...wrapperProps}
                style={{
                  ...baseStyles,
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {dynamicSnippetData.map((item, index) => {
                  if (dataSource === "products") {
                    const image =
                      item.images?.find((img: any) => img.isPrimary) ||
                      item.images?.[0];
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.productCard,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const locationId = location?.locationId;
                    const { isPluginEnabled } = storeContext || {};

                    // Find the index of the selected image
                    let imageIndex: number | undefined;
                    if (image && item.images) {
                      const foundImage = item.images.find(
                        (img: any) => img.url === image.url || img === image,
                      );
                      if (foundImage) {
                        imageIndex = item.images.indexOf(foundImage);
                      }
                    }

                    const pricing = calculateProductCardPricing(
                      item,
                      locationId,
                      isPluginEnabled,
                      imageIndex,
                    );
                    const { price, originalPrice } = pricing;

                    return (
                      <div
                        key={item._id || index}
                        style={{ display: "flex", gap: "1rem" }}
                      >
                        <img
                          src={imageUrl}
                          alt={item.title}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "0.25rem",
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <h3
                            style={{
                              fontSize: "1.125rem",
                              fontWeight: "bold",
                              marginBottom: "0.5rem",
                            }}
                          >
                            {item.title}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "1.25rem",
                                fontWeight: "bold",
                                color: "#059669",
                              }}
                            >
                              {location?.currencySymbol || "$"}
                              {price.toFixed(2)}
                            </p>
                            {originalPrice && originalPrice > price && (
                              <p
                                style={{
                                  fontSize: "1rem",
                                  color: "#6b7280",
                                  textDecoration: "line-through",
                                }}
                              >
                                {location?.currencySymbol || "$"}
                                {originalPrice.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  } else if (dataSource === "categories") {
                    return (
                      <div key={item._id || index}>
                        <h3
                          style={{
                            fontSize: "1.125rem",
                            fontWeight: "bold",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {item.name}
                        </h3>
                        {item.description && (
                          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          }
          case "card": {
            // For user-facing pages, don't include default editor styles
            const baseStyles = !isPreview ? {} : styles;
            return (
              <div
                {...wrapperProps}
                style={{
                  ...baseStyles,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1rem",
                }}
              >
                {dynamicSnippetData.map((item, index) => {
                  if (dataSource === "products") {
                    const image =
                      item.images?.find((img: any) => img.isPrimary) ||
                      item.images?.[0];
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.productCard,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const locationId = location?.locationId;

                    return (
                      <div key={item._id || index}>
                        <ProductCard
                          id={item._id}
                          title={item.title}
                          image={imageUrl}
                          currency={location?.currency || "USD"}
                          locationId={locationId}
                          product={item}
                          href={`/products/${item.handle || item._id}`}
                        />
                      </div>
                    );
                  } else if (dataSource === "categories") {
                    return (
                      <div key={item._id || index}>
                        <h3
                          style={{
                            fontSize: "1.25rem",
                            fontWeight: "bold",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {item.name}
                        </h3>
                        {item.description && (
                          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          }
          default:
            return (
              <div {...wrapperProps}>
                {dynamicSnippetData.map((item, index) => (
                  <div key={index}>{JSON.stringify(item)}</div>
                ))}
              </div>
            );
        }
      }
      default:
        return (
          <div style={styles} {...(className && { className })}>
            Unknown component: {component.type}
          </div>
        );
    }
  };

  return <>{renderComponent()}</>;
};
